<template>
  <div class="plus-pwf">
    <div class="plus-pwf-item">
      <button
        v-if="mode === 'canvas'"
        ref="nodeBtn"
        type="button"
        class="plus-prompt-chip plus-prompt-chip--pill plus-pwf-model"
        title="选择画布节点类型"
        @click.stop="openNodePicker"
      >
        <span class="plus-pwf-model__icon">
          <i :class="[selectedNodeIcon, 'text-[10px] text-fuchsia-200/90']"></i>
        </span>
        <span class="plus-pwf-model__label">{{ selectedNodeLabel }}</span>
        <i class="fas fa-chevron-down text-[9px] opacity-50"></i>
      </button>
      <button
        v-else
        ref="modelBtn"
        type="button"
        class="plus-prompt-chip plus-prompt-chip--pill plus-pwf-model"
        title="选择模型（与对话工作流一致）"
        @click.stop="openModelPicker"
      >
        <span class="plus-pwf-model__icon">
          <i class="fas fa-cube text-[10px] text-fuchsia-200/90"></i>
        </span>
        <span class="plus-pwf-model__label">{{ selectedModelName }}</span>
        <i class="fas fa-chevron-down text-[9px] opacity-50"></i>
      </button>
    </div>

    <div class="plus-pwf-item">
      <button
        ref="styleBtn"
        type="button"
        class="plus-prompt-chip plus-prompt-chip--pill plus-pwf-style-btn"
        :title="selectedStyle ? `风格：${selectedStyle.name}` : '选择风格（与对话工作流一致）'"
        @click.stop="openStyleLibrary"
      >
        <i class="fas fa-palette text-[11px] opacity-70"></i>
        <span class="plus-pwf-style__label">{{
          selectedStyle ? selectedStyle.name : '风格'
        }}</span>
      </button>
    </div>

    <div class="plus-pwf-item">
      <button
        ref="assetBtn"
        type="button"
        class="plus-prompt-chip plus-prompt-chip--round"
        title="资产"
        @click.stop="openAssetPanel"
      >
        <i class="far fa-smile text-[13px]"></i>
      </button>
    </div>

    <div class="plus-pwf-item">
      <button
        type="button"
        class="plus-prompt-chip plus-prompt-chip--round"
        title="重置"
        @click="$emit('reset')"
      >
        <i class="fas fa-rotate-left text-[13px]"></i>
      </button>
    </div>

    <WorkflowCanvasNodePickerModal
      v-if="mode === 'canvas'"
      :visible="showNodePicker"
      :anchor-rect="anchorRect"
      :node-type="selectedNodeType"
      @close="showNodePicker = false"
      @select="selectCanvasNode"
    />
    <WorkflowModelPickerModal
      v-else
      :visible="showModelPicker"
      :anchor-rect="anchorRect"
      :model-id="selectedModelId"
      @close="showModelPicker = false"
      @select="selectModel"
    />
    <WorkflowStyleLibraryModal
      :visible="showStyleLibrary"
      :anchor-rect="anchorRect"
      :selected-style="selectedStyle"
      @close="showStyleLibrary = false"
      @select="selectStyle"
    />
    <WorkflowAssetPanelModal
      :visible="showAssetPanel"
      :anchor-rect="anchorRect"
      @close="showAssetPanel = false"
      @pick-asset="onAssetPicked"
    />
  </div>
</template>

<script>
import WorkflowAssetPanelModal from '@/components/WorkflowAssetPanelModal.vue'
import WorkflowCanvasNodePickerModal from '@/components/WorkflowCanvasNodePickerModal.vue'
import WorkflowModelPickerModal from '@/components/WorkflowModelPickerModal.vue'
import WorkflowStyleLibraryModal from '@/components/WorkflowStyleLibraryModal.vue'
import {
  CANVAS_LAUNCH_NODE_OPTIONS,
  DEFAULT_CANVAS_LAUNCH_NODE_TYPE,
  canvasLaunchNodeLabel
} from '@/constants/canvasLaunchNodeOptions'
import {
  WORKFLOW_MODEL_OPTIONS,
  DEFAULT_WORKFLOW_MODEL_ID
} from '@/constants/workflowModelOptions'
import { getAnchorRect } from '@/utils/workflowPopoverAnchor'
import { hasAuthToken, openLoginModal } from '@/utils/loginGate'
import { showErrorToast } from '@/utils/toast'

const LS_MODEL = 'plus_prompt_model_id'
const LS_NODE = 'plus_prompt_canvas_node_type'
const LS_STYLE = 'plus_prompt_style'

export default {
  name: 'PlusPromptWorkflowControls',
  components: {
    WorkflowCanvasNodePickerModal,
    WorkflowModelPickerModal,
    WorkflowStyleLibraryModal,
    WorkflowAssetPanelModal
  },
  props: {
    /** workflow：对话工作流模型；canvas：画布节点类型 */
    mode: {
      type: String,
      default: 'workflow'
    },
    value: {
      type: Object,
      default: () => ({ modelId: DEFAULT_WORKFLOW_MODEL_ID, style: null })
    }
  },
  data() {
    return {
      modelOptions: WORKFLOW_MODEL_OPTIONS,
      canvasNodeOptions: CANVAS_LAUNCH_NODE_OPTIONS,
      selectedModelId: DEFAULT_WORKFLOW_MODEL_ID,
      selectedNodeType: DEFAULT_CANVAS_LAUNCH_NODE_TYPE,
      selectedStyle: null,
      showModelPicker: false,
      showNodePicker: false,
      showStyleLibrary: false,
      showAssetPanel: false,
      anchorRect: null,
      _anchorRefreshHandler: null
    }
  },
  computed: {
    selectedModelName() {
      const m = this.modelOptions.find((x) => x.id === this.selectedModelId)
      return (m && m.name) || '选择模型'
    },
    selectedNodeLabel() {
      return canvasLaunchNodeLabel(this.selectedNodeType)
    },
    selectedNodeIcon() {
      const row = this.canvasNodeOptions.find(x => x.type === this.selectedNodeType)
      return (row && row.icon) || 'fas fa-cube'
    },
    anyModalOpen() {
      return (
        this.showModelPicker ||
        this.showNodePicker ||
        this.showStyleLibrary ||
        this.showAssetPanel
      )
    }
  },
  mounted() {
    this.hydrateFromStorage()
    document.addEventListener('keydown', this.onGlobalKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onGlobalKeydown)
    this.unbindAnchorRefresh()
    this.closeAllModals()
  },
  methods: {
    hydrateFromStorage() {
      try {
        if (this.mode === 'canvas') {
          const nt = localStorage.getItem(LS_NODE)
          if (nt && this.canvasNodeOptions.some(o => o.type === nt)) {
            this.selectedNodeType = nt
          } else if (this.value && this.value.nodeType) {
            this.selectedNodeType = this.value.nodeType
          }
        } else {
          const mid = localStorage.getItem(LS_MODEL)
          if (mid && this.modelOptions.some((m) => m.id === mid)) {
            this.selectedModelId = mid
          } else if (this.value && this.value.modelId) {
            this.selectedModelId = this.value.modelId
          }
        }
        const raw = localStorage.getItem(LS_STYLE)
        if (raw) {
          const s = JSON.parse(raw)
          if (s && s.id && s.name) this.selectedStyle = s
        } else if (this.value && this.value.style) {
          this.selectedStyle = this.value.style
        }
      } catch (e) {
        /* ignore */
      }
      this.emitChange()
    },
    emitChange() {
      const model = this.modelOptions.find((m) => m.id === this.selectedModelId) || null
      const nodeOpt =
        this.canvasNodeOptions.find(o => o.type === this.selectedNodeType) || null
      const payload = {
        modelId: this.selectedModelId,
        model,
        nodeType: this.selectedNodeType,
        nodeOption: nodeOpt,
        style: this.selectedStyle
      }
      this.$emit('input', payload)
      this.$emit('change', payload)
    },
    persist() {
      try {
        if (this.mode === 'canvas') {
          localStorage.setItem(LS_NODE, this.selectedNodeType)
        } else {
          localStorage.setItem(LS_MODEL, this.selectedModelId)
        }
        if (this.selectedStyle) {
          localStorage.setItem(LS_STYLE, JSON.stringify(this.selectedStyle))
        } else {
          localStorage.removeItem(LS_STYLE)
        }
      } catch (e) {
        /* ignore */
      }
    },
    closeAllModals() {
      this.showModelPicker = false
      this.showNodePicker = false
      this.showStyleLibrary = false
      this.showAssetPanel = false
      this.unbindAnchorRefresh()
      this.anchorRect = null
    },
    captureAnchor(refName) {
      const el = this.$refs[refName]
      const node = el && (el.$el || el)
      this.anchorRect = getAnchorRect(node)
    },
    refreshAnchorIfOpen() {
      if (this.showNodePicker) this.captureAnchor('nodeBtn')
      else if (this.showModelPicker) this.captureAnchor('modelBtn')
      else if (this.showStyleLibrary) this.captureAnchor('styleBtn')
      else if (this.showAssetPanel) this.captureAnchor('assetBtn')
    },
    bindAnchorRefresh() {
      this.unbindAnchorRefresh()
      this._anchorRefreshHandler = () => this.refreshAnchorIfOpen()
      window.addEventListener('resize', this._anchorRefreshHandler, { passive: true })
      window.addEventListener('scroll', this._anchorRefreshHandler, { passive: true, capture: true })
    },
    unbindAnchorRefresh() {
      if (!this._anchorRefreshHandler) return
      window.removeEventListener('resize', this._anchorRefreshHandler)
      window.removeEventListener('scroll', this._anchorRefreshHandler, true)
      this._anchorRefreshHandler = null
    },
    openModalWithAnchor(refName, openKey) {
      this.showModelPicker = false
      this.showNodePicker = false
      this.showStyleLibrary = false
      this.showAssetPanel = false
      this.unbindAnchorRefresh()
      this.captureAnchor(refName)
      this[openKey] = true
      this.bindAnchorRefresh()
    },
    onGlobalKeydown(e) {
      if (!e || e.key !== 'Escape' || !this.anyModalOpen) return
      this.closeAllModals()
    },
    openNodePicker() {
      if (this.showNodePicker) {
        this.closeAllModals()
        return
      }
      this.$nextTick(() => {
        this.openModalWithAnchor('nodeBtn', 'showNodePicker')
      })
    },
    openModelPicker() {
      this.openModalWithAnchor('modelBtn', 'showModelPicker')
    },
    openStyleLibrary() {
      this.openModalWithAnchor('styleBtn', 'showStyleLibrary')
    },
    openAssetPanel() {
      if (!hasAuthToken()) {
        showErrorToast('请先登录')
        openLoginModal()
        return
      }
      if (this.showAssetPanel) {
        this.closeAllModals()
        return
      }
      this.$nextTick(() => {
        this.openModalWithAnchor('assetBtn', 'showAssetPanel')
      })
    },
    selectModel(m) {
      if (!m || !m.id) return
      this.selectedModelId = m.id
      this.persist()
      this.emitChange()
    },
    selectCanvasNode(opt) {
      if (!opt || !opt.type) return
      this.selectedNodeType = opt.type
      this.persist()
      this.emitChange()
    },
    selectStyle(s) {
      this.selectedStyle = s ? { ...s } : null
      this.persist()
      this.emitChange()
    },
    onAssetPicked(a) {
      this.showAssetPanel = false
      this.$emit('pick-asset', a)
    },
    resetWorkflow() {
      if (this.mode === 'canvas') {
        this.selectedNodeType = DEFAULT_CANVAS_LAUNCH_NODE_TYPE
      } else {
        this.selectedModelId = DEFAULT_WORKFLOW_MODEL_ID
      }
      this.selectedStyle = null
      this.persist()
      this.emitChange()
    }
  }
}
</script>

<style scoped>
.plus-pwf {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
}

.plus-pwf-item {
  position: relative;
  flex-shrink: 0;
}

.plus-pwf-model {
  padding-left: 10px;
  padding-right: 14px;
}

.plus-pwf-model__icon {
  display: flex;
  height: 20px;
  width: 20px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.08);
}

.plus-pwf-model__label {
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plus-pwf-style-btn .fa-palette {
  color: rgba(156, 163, 175, 0.95);
}

.plus-pwf-style__label {
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
